import React, { useCallback, useEffect, useRef, useState } from 'react';
import { faCheck, faUndo, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { apiJson } from '../../../components/apiClient';
import { Button } from '../ui/Button';
import { Badge, StatusBadge } from '../ui/Badge';
import { Sheet } from '../ui/Sheet';
import { RowMenu } from '../ui/RowMenu';
import { Panel } from '../ui/Panel';
import { EntityLink, UnlinkedField } from '../ui/EntityLink';
import { CobrancaWizardSheet } from './CobrancaWizardSheet';
import { VincularProjetoSelector } from './VincularProjetoSelector';
import { LancamentoSheet } from './LancamentoSheet';

const apiFin = (path, opts) => apiJson(`/financeiro${path}`, opts);

const fmtBRL = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};
const fmtCompetencia = (c) => {
  if (!c) return '—';
  const [ano, mes] = c.split('-');
  return new Date(Number(ano), Number(mes) - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
};
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');

const STATUS_ASSINATURA_TONE = { ATIVA: 'positive', PAUSADA: 'warning', CANCELADA: 'neutral', ARQUIVADA: 'neutral' };

// Dropdown "Gerar competências ▾" (§6 da correção pós-QA): agrupa
// Histórico/Futuro num único botão descoberto, em vez de dois botões soltos.
function GerarCompetenciasDropdown({ onHistorico, onFuturo }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, []);

  return (
    <div className="crm-dropdown" ref={ref}>
      <Button size="sm" variant="outline" icon={faChevronDown} onClick={() => setOpen((v) => !v)}>Gerar competências</Button>
      {open && (
        <div className="crm-dropdown-menu align-left">
          <button type="button" className="crm-dropdown-item" onClick={() => { setOpen(false); onHistorico(); }}>Histórico (retroativo)</button>
          <button type="button" className="crm-dropdown-item" onClick={() => { setOpen(false); onFuturo(); }}>Futuro (projeção)</button>
        </div>
      )}
    </div>
  );
}

// Detalhe de assinatura (§20 do redesign) — abrir/editar/pausar/cancelar/ver
// competências/gerar histórico/gerar futuro/registrar pagamento/estornar.
// Componente compartilhado: usado dentro do Cliente (Etapa B) e será
// reaproveitado na tela própria de Financeiro > Assinaturas (Etapa D).
export function AssinaturaSheet({ assinaturaId, open, onClose, onChanged, showToast, onNavegar }) {
  const [regra, setRegra] = useState(null);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [showHistorico, setShowHistorico] = useState(false);
  const [showFuturo, setShowFuturo] = useState(false);
  const [histInicio, setHistInicio] = useState('');
  const [histFim, setHistFim] = useState('');
  const [histStatus, setHistStatus] = useState('PAGO');
  const [futuroMeses, setFuturoMeses] = useState('3');
  const [showVincularProjeto, setShowVincularProjeto] = useState(false);
  const vincularProjetoBtnRef = useRef(null);
  const [competenciaSelecionada, setCompetenciaSelecionada] = useState(null);
  const [showCobranca, setShowCobranca] = useState(false);

  const carregar = useCallback(async () => {
    if (!assinaturaId) return;
    const r = await apiFin(`/recurrence-rules/${assinaturaId}`);
    if (r.status === 'ok') {
      setRegra(r.regra);
      setForm({
        descricao: r.regra.descricao, categoria: r.regra.categoria || '', valor: r.regra.valor,
        dia_vencimento: r.regra.dia_vencimento, forma_pagamento: r.regra.forma_pagamento || '',
        data_fim: r.regra.data_fim || '',
        contato_nome: r.regra.contato_cobranca?.nome || '', contato_telefone: r.regra.contato_cobranca?.telefone || '',
        contato_email: r.regra.contato_cobranca?.email || '',
      });
    } else showToast?.(r.mensagem || 'Erro ao carregar assinatura', 'error');
  }, [assinaturaId, showToast]);

  useEffect(() => { if (open) carregar(); }, [open, carregar]);

  const notificarMudanca = () => { carregar(); onChanged?.(); };

  const salvarEdicao = async () => {
    setSalvando(true);
    const r = await apiFin(`/recurrence-rules/${assinaturaId}`, {
      method: 'PUT',
      body: JSON.stringify({
        descricao: form.descricao, categoria: form.categoria || null, valor: form.valor,
        dia_vencimento: parseInt(form.dia_vencimento, 10), forma_pagamento: form.forma_pagamento || null,
        data_fim: form.data_fim || null,
        contato_cobranca: { nome: form.contato_nome, telefone: form.contato_telefone, email: form.contato_email },
      }),
    });
    setSalvando(false);
    if (r.status === 'ok') { showToast?.('Assinatura atualizada!', 'success'); setEditando(false); notificarMudanca(); }
    else showToast?.(r.mensagem || 'Erro ao salvar', 'error');
  };

  const pausarOuRetomar = async () => {
    const acao = regra.status === 'PAUSADA' ? 'retomar' : 'pausar';
    const r = await apiFin(`/recurrence-rules/${assinaturaId}/${acao}`, { method: 'POST' });
    if (r.status === 'ok') { showToast?.(acao === 'pausar' ? 'Assinatura pausada.' : 'Assinatura retomada.', 'success'); notificarMudanca(); }
    else showToast?.(r.mensagem || 'Erro', 'error');
  };

  const vincularProjeto = async (projetoId) => {
    const r = await apiFin(`/recurrence-rules/${assinaturaId}`, { method: 'PUT', body: JSON.stringify({ projeto_id: projetoId }) });
    if (r.status === 'ok') { showToast?.(projetoId ? 'Projeto vinculado!' : 'Vínculo com projeto removido.', 'success'); setShowVincularProjeto(false); notificarMudanca(); }
    else showToast?.(r.mensagem || 'Erro ao vincular projeto', 'error');
  };

  const cancelar = async () => {
    const { isConfirmed } = await Swal.fire({ title: 'Cancelar esta assinatura?', text: 'Para de gerar novas competências. As já geradas continuam normalmente. Pode ser desfeito depois.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Cancelar assinatura', cancelButtonText: 'Voltar' });
    if (!isConfirmed) return;
    const r = await apiFin(`/recurrence-rules/${assinaturaId}`, { method: 'DELETE' });
    if (r.status === 'ok') { showToast?.('Assinatura cancelada. Use "Reativar" para desfazer.', 'success'); notificarMudanca(); }
    else showToast?.(r.mensagem || 'Erro', 'error');
  };

  const reativarCancelada = async () => {
    const r = await apiFin(`/recurrence-rules/${assinaturaId}/reativar-cancelada`, { method: 'POST' });
    if (r.status === 'ok') { showToast?.('Cancelamento desfeito!', 'success'); notificarMudanca(); }
    else showToast?.(r.mensagem || 'Erro ao reativar', 'error');
  };

  const arquivar = async () => {
    const { isConfirmed } = await Swal.fire({ title: 'Arquivar esta assinatura?', text: 'Ela some da lista normal, mas nada é apagado — o histórico de competências é preservado.', icon: 'question', showCancelButton: true, confirmButtonText: 'Arquivar', cancelButtonText: 'Voltar' });
    if (!isConfirmed) return;
    const r = await apiFin(`/recurrence-rules/${assinaturaId}/arquivar`, { method: 'POST' });
    if (r.status === 'ok') { showToast?.('Assinatura arquivada.', 'success'); notificarMudanca(); }
    else showToast?.(r.mensagem || 'Erro ao arquivar', 'error');
  };

  const excluirDefinitivamente = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Excluir definitivamente?', icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Excluir definitivamente', cancelButtonText: 'Voltar', confirmButtonColor: '#dc2626',
      html: `Isso remove por completo a assinatura <strong>${regra?.descricao || ''}</strong>.<br/>Só é permitido quando nenhuma competência jamais foi gerada — não pode ser desfeito.`,
    });
    if (!isConfirmed) return;
    const r = await apiFin(`/recurrence-rules/${assinaturaId}/excluir`, { method: 'POST' });
    if (r.status === 'ok') { showToast?.('Assinatura excluída definitivamente.', 'success'); onChanged?.(); onClose(); }
    else showToast?.(r.mensagem || 'Erro ao excluir', 'error');
  };

  const gerarHistorico = async () => {
    if (!histInicio || !histFim) { showToast?.('Informe início e fim', 'error'); return; }
    const r = await apiFin(`/recurrence-rules/${assinaturaId}/gerar-historico`, {
      method: 'POST', body: JSON.stringify({ competencia_inicio: histInicio, competencia_fim: histFim, status: histStatus }),
    });
    if (r.status === 'ok') { showToast?.(`${r.competencias_geradas} competência(s) gerada(s).`, 'success'); setShowHistorico(false); notificarMudanca(); }
    else showToast?.(r.mensagem || 'Erro ao gerar histórico', 'error');
  };

  const gerarFuturo = async () => {
    const r = await apiFin(`/recurrence-rules/${assinaturaId}/gerar-futuro`, { method: 'POST', body: JSON.stringify({ meses: parseInt(futuroMeses, 10) }) });
    if (r.status === 'ok') { showToast?.(`${r.competencias_geradas} competência(s) futura(s) gerada(s).`, 'success'); setShowFuturo(false); notificarMudanca(); }
    else showToast?.(r.mensagem || 'Erro ao gerar futuro', 'error');
  };

  const registrarPagamento = async (competenciaId, valor) => {
    const r = await apiFin(`/receivables/${competenciaId}/pagamentos`, {
      method: 'POST', body: JSON.stringify({ valor, data_pagamento: new Date().toISOString().slice(0, 10) }),
    });
    if (r.status === 'ok') { showToast?.('Pagamento registrado!', 'success'); notificarMudanca(); }
    else showToast?.(r.mensagem || 'Erro ao registrar pagamento', 'error');
  };

  const estornarPagamento = async (competenciaId) => {
    const det = await apiFin(`/receivables/${competenciaId}`);
    const pagamento = det.receivable?.pagamentos?.find((p) => p.status === 'CONFIRMADO');
    if (!pagamento) { showToast?.('Nenhum pagamento confirmado encontrado', 'error'); return; }
    const r = await apiFin(`/receivables/${competenciaId}/pagamentos/${pagamento.id}`, {
      method: 'DELETE', body: JSON.stringify({ motivo: 'Estorno via assinatura' }),
    });
    if (r.status === 'ok') { showToast?.('Pagamento estornado.', 'success'); notificarMudanca(); }
    else showToast?.(r.mensagem || 'Erro ao estornar', 'error');
  };

  if (!open) return null;

  return (
    <Sheet open={open} onClose={onClose} title={regra ? regra.descricao : 'Assinatura'} subtitle={regra ? `${fmtBRL(regra.valor)} / mês — dia ${regra.dia_vencimento}` : ''}>
      {!regra ? (
        <div className="crm-form-hint">Carregando...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
            <Badge tone={STATUS_ASSINATURA_TONE[regra.status]}>
              {regra.status === 'ATIVA' ? 'Ativa' : regra.status === 'PAUSADA' ? 'Pausada' : regra.status === 'ARQUIVADA' ? 'Arquivada' : 'Cancelada'}
            </Badge>
            <Button size="sm" variant="outline" onClick={() => setEditando((v) => !v)}>{editando ? 'Fechar edição' : 'Editar'}</Button>
            {regra.status === 'CANCELADA' && (
              <Button size="sm" variant="primary" onClick={reativarCancelada}>Reativar (desfazer cancelamento)</Button>
            )}
            <GerarCompetenciasDropdown onHistorico={() => setShowHistorico(true)} onFuturo={() => setShowFuturo(true)} />
            {regra.tipo === 'RECEITA' && (
              <Button size="sm" variant="outline" onClick={() => setShowCobranca(true)}>Configurar cobrança</Button>
            )}
            <RowMenu itens={[
              { label: 'Retomar', onClick: pausarOuRetomar, visivel: regra.status === 'PAUSADA' },
              { label: 'Pausar', onClick: pausarOuRetomar, visivel: regra.status === 'ATIVA' },
              { label: 'Cancelar', onClick: cancelar, visivel: regra.status === 'ATIVA' || regra.status === 'PAUSADA' },
              { label: 'Arquivar', onClick: arquivar, visivel: regra.status !== 'ARQUIVADA' },
              {
                label: 'Excluir definitivamente', tone: 'danger', onClick: excluirDefinitivamente,
                disabled: (regra.competencias || []).some((c) => c.status === 'PAGO'),
                hint: (regra.competencias || []).some((c) => c.status === 'PAGO') ? 'Há competência já paga — use Arquivar' : undefined,
              },
            ]} />
          </div>

          <Panel title="Contexto">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)' }}>
                <span className="crm-form-hint" style={{ minWidth: '80px' }}>Cliente</span>
                {regra.cliente_id ? (
                  <EntityLink onClick={() => onNavegar?.('clientes', { clienteId: regra.cliente_id })}>{regra.cliente_nome || 'Cliente'}</EntityLink>
                ) : (
                  <UnlinkedField />
                )}
              </div>
              <div ref={vincularProjetoBtnRef} style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)' }}>
                <span className="crm-form-hint" style={{ minWidth: '80px' }}>Projeto</span>
                {regra.projeto_id ? (
                  <>
                    <EntityLink onClick={() => onNavegar?.('projetos', { projetoId: regra.projeto_id })}>{regra.projeto_nome || 'Projeto'}</EntityLink>
                    <button type="button" className="crm-entity-link" onClick={() => setShowVincularProjeto((v) => !v)}>Alterar</button>
                  </>
                ) : (
                  <UnlinkedField actionLabel="+ Vincular" onAction={() => setShowVincularProjeto((v) => !v)} />
                )}
                {showVincularProjeto && (
                  <VincularProjetoSelector
                    anchorRef={vincularProjetoBtnRef}
                    clienteId={regra.cliente_id}
                    projetoAtualId={regra.projeto_id}
                    onSelecionar={vincularProjeto}
                    onFechar={() => setShowVincularProjeto(false)}
                  />
                )}
              </div>
            </div>
          </Panel>

          {editando && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-3)' }}>
              <div className="crm-form-field">
                <label className="crm-form-label">Descrição</label>
                <input className="crm-input" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
              </div>
              <div className="crm-form-field">
                <label className="crm-form-label">Categoria</label>
                <input className="crm-input" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} />
              </div>
              <div className="crm-form-field">
                <label className="crm-form-label">Valor</label>
                <input className="crm-input" type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} />
              </div>
              <div className="crm-form-field">
                <label className="crm-form-label">Dia de vencimento</label>
                <input className="crm-input" type="number" min="1" max="31" value={form.dia_vencimento} onChange={(e) => setForm({ ...form, dia_vencimento: e.target.value })} />
              </div>
              <div className="crm-form-field">
                <label className="crm-form-label">Forma de pagamento</label>
                <input className="crm-input" value={form.forma_pagamento} onChange={(e) => setForm({ ...form, forma_pagamento: e.target.value })} placeholder="Pix, Boleto..." />
              </div>
              <div className="crm-form-field">
                <label className="crm-form-label">Data de fim (opcional)</label>
                <input className="crm-input" type="date" value={form.data_fim || ''} onChange={(e) => setForm({ ...form, data_fim: e.target.value })} />
              </div>
              <div className="crm-form-field">
                <label className="crm-form-label">Contato de cobrança — nome</label>
                <input className="crm-input" value={form.contato_nome} onChange={(e) => setForm({ ...form, contato_nome: e.target.value })} />
              </div>
              <div className="crm-form-field">
                <label className="crm-form-label">Telefone/WhatsApp</label>
                <input className="crm-input" value={form.contato_telefone} onChange={(e) => setForm({ ...form, contato_telefone: e.target.value })} />
              </div>
              <div className="crm-form-field">
                <label className="crm-form-label">E-mail</label>
                <input className="crm-input" type="email" value={form.contato_email} onChange={(e) => setForm({ ...form, contato_email: e.target.value })} />
              </div>
              <Button variant="primary" loading={salvando} onClick={salvarEdicao}>Salvar</Button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
            <Button size="sm" variant="outline" onClick={() => setShowHistorico(true)}>Gerar histórico</Button>
            <Button size="sm" variant="outline" onClick={() => setShowFuturo(true)}>Gerar futuro</Button>
          </div>

          {showHistorico && (
            <div className="crm-panel" style={{ padding: 'var(--crm-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-3)' }}>
              <div className="crm-form-hint">Gerar competências passadas de uma vez (ex.: Jan/2026 a Ago/2026 como Pago)</div>
              <div className="crm-form-field">
                <label className="crm-form-label">De (AAAA-MM)</label>
                <input className="crm-input" placeholder="2026-01" value={histInicio} onChange={(e) => setHistInicio(e.target.value)} />
              </div>
              <div className="crm-form-field">
                <label className="crm-form-label">Até (AAAA-MM)</label>
                <input className="crm-input" placeholder="2026-08" value={histFim} onChange={(e) => setHistFim(e.target.value)} />
              </div>
              <div className="crm-form-field">
                <label className="crm-form-label">Status</label>
                <select className="crm-select" value={histStatus} onChange={(e) => setHistStatus(e.target.value)}>
                  <option value="PAGO">Pago</option>
                  <option value="PENDENTE">Pendente</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
                <Button variant="primary" size="sm" onClick={gerarHistorico}>Gerar</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowHistorico(false)}>Cancelar</Button>
              </div>
            </div>
          )}

          {showFuturo && (
            <div className="crm-panel" style={{ padding: 'var(--crm-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-3)' }}>
              <div className="crm-form-field">
                <label className="crm-form-label">Gerar próximos</label>
                <select className="crm-select" value={futuroMeses} onChange={(e) => setFuturoMeses(e.target.value)}>
                  <option value="3">3 meses</option>
                  <option value="6">6 meses</option>
                  <option value="12">12 meses</option>
                  <option value="24">24 meses</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
                <Button variant="primary" size="sm" onClick={gerarFuturo}>Gerar</Button>
                <Button variant="ghost" size="sm" onClick={() => setShowFuturo(false)}>Cancelar</Button>
              </div>
            </div>
          )}

          <div className="crm-panel">
            <div className="crm-panel-header"><h4 className="crm-panel-title">Competências</h4></div>
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead><tr><th>Competência</th><th>Vencimento</th><th>Valor</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {regra.competencias.map((c) => (
                    <tr key={c.id} className="clickable" onClick={() => setCompetenciaSelecionada({ id: c.id, descricao: `${regra.descricao} — ${fmtCompetencia(c.competencia)}`, tipo: regra.tipo === 'RECEITA' ? 'ASSINATURA' : 'DESPESA', _origem: regra.tipo === 'RECEITA' ? 'receivable' : 'payable' })}>
                      <td className="crm-table-primary" style={{ textTransform: 'capitalize' }}>{fmtCompetencia(c.competencia)}</td>
                      <td>{fmtDate(c.vencimento)}</td>
                      <td>{fmtBRL(c.valor)}</td>
                      <td><StatusBadge status={c.status} /></td>
                      <td className="crm-table-actions-cell" onClick={(e) => e.stopPropagation()}>
                        {(c.status === 'PENDENTE' || c.status === 'PARCIAL') && (
                          <Button size="sm" variant="ghost" icon={faCheck} onClick={() => registrarPagamento(c.id, c.valor)}>Pagar valor cheio</Button>
                        )}
                        {c.status === 'PAGO' && (
                          <Button size="sm" variant="ghost" icon={faUndo} onClick={() => estornarPagamento(c.id)}>Estornar</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {showCobranca && (
        <CobrancaWizardSheet
          receivableIdInicial={(regra.competencias || []).find((c) => c.status === 'PENDENTE' || c.status === 'PARCIAL')?.id || null}
          onClose={() => setShowCobranca(false)}
          onSalvo={() => { setShowCobranca(false); notificarMudanca(); }}
          showToast={showToast}
        />
      )}
      {competenciaSelecionada && (
        <LancamentoSheet
          item={competenciaSelecionada}
          onClose={() => setCompetenciaSelecionada(null)}
          onChanged={notificarMudanca}
          showToast={showToast}
          onNavegar={onNavegar}
          onAbrirAssinatura={() => {}}
        />
      )}
    </Sheet>
  );
}

export default AssinaturaSheet;
